import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, VStack, Link, useMultiStyleConfig, HStack} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {getFutureStockDate} from '../../utils/order-availability-utils'
import {remCalc} from '../../theme/_utils'
import {useCustomPreferenceWithFallback} from '../../hooks/use-custom-preference'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * PreorderBackorderLabel component
 * Displays labels for products available for preorder or backorder
 */
const PreorderBackorderLabel = ({product, locale, variant = 'preorder'}) => {
    const style = useMultiStyleConfig('ProductView')
    const backorderRedirectPDP = useCustomPreferenceWithFallback('backorderRedirectPDP')
    const {locale: siteLocale} = useMultiSite()
    const localeId = siteLocale?.alias || locale
    const localizedBackorderLink = backorderRedirectPDP
        ? `/${localeId}${
              backorderRedirectPDP.startsWith('/')
                  ? backorderRedirectPDP
                  : `/${backorderRedirectPDP}`
          }`
        : null
    if (variant === 'preorder') {
        const preorderDate = getFutureStockDate(product, locale)

        return (
            <Box mb={remCalc(8)}>
                <VStack {...style.preorderLabel}>
                    <Text {...style.preorderLabelTitle}>
                        <FormattedMessage
                            id="product_view.preorder.title"
                            defaultMessage="Available for Preorder"
                        />
                    </Text>
                    {preorderDate ? (
                        <HStack gap={remCalc(4)}>
                            <Text {...style.preorderLabelDate}>
                                <FormattedMessage
                                    id="order_summary.message.available_from"
                                    defaultMessage="This item will be available from {date}."
                                    values={{
                                        date: (
                                            <Text as="span" fontWeight="bold">
                                                {preorderDate}
                                            </Text>
                                        ),
                                    }}
                                />
                            </Text>
                            <Text {...style.backorderLabelDate}>
                                <Link
                                    {...style.backorderDiscoverLink}
                                    href={localizedBackorderLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FormattedMessage
                                        id="product_view.backorder.discover_more"
                                        defaultMessage="Discover more"
                                    />
                                </Link>
                            </Text>
                        </HStack>
                    ) : (
                        <HStack gap={remCalc(4)}>
                            <Text {...style.preorderLabelDate}>
                                <FormattedMessage
                                    id="product_view.preorder.available_soon"
                                    defaultMessage="This item will be available soon."
                                />
                            </Text>
                            <Text {...style.backorderLabelDate}>
                                <Link
                                    {...style.backorderDiscoverLink}
                                    href={localizedBackorderLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FormattedMessage
                                        id="product_view.backorder.discover_more"
                                        defaultMessage="Discover more"
                                    />
                                </Link>
                            </Text>
                        </HStack>
                    )}
                </VStack>
            </Box>
        )
    }

    if (variant === 'backorder') {
        const backorderDate = getFutureStockDate(product, locale)

        return (
            <Box mb={remCalc(8)}>
                <VStack {...style.preorderLabel}>
                    {backorderDate ? (
                        <Text {...style.backorderLabelTitle}>
                            <FormattedMessage
                                id="product_view.backorder.available_from_date"
                                defaultMessage="Available from {date}"
                                values={{date: backorderDate}}
                            />
                        </Text>
                    ) : (
                        <Text {...style.backorderLabelTitle}>
                            <FormattedMessage
                                id="product_view.backorder.available_soon"
                                defaultMessage="Available soon"
                            />
                        </Text>
                    )}
                    <Text {...style.backorderLabelDate}>
                        <FormattedMessage
                            id="product_view.backorder.order_today"
                            defaultMessage="You can order it today. {link}"
                            values={{
                                link: (
                                    <Link
                                        {...style.backorderDiscoverLink}
                                        href={localizedBackorderLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FormattedMessage
                                            id="product_view.backorder.discover_more"
                                            defaultMessage="Discover more"
                                        />
                                    </Link>
                                ),
                            }}
                        />
                    </Text>
                </VStack>
            </Box>
        )
    }

    return null
}

PreorderBackorderLabel.propTypes = {
    product: PropTypes.object.isRequired,
    locale: PropTypes.string,
    variant: PropTypes.oneOf(['preorder', 'backorder']).isRequired,
}

PreorderBackorderLabel.defaultProps = {
    locale: 'en-GB',
}

export default PreorderBackorderLabel
